import { graphql } from '@predium/client-graphql';

graphql(`
  fragment CostByRenovationCategory on CostByRenovationCategoryOutput {
    series {
      name
      data {
        values
        actionsCount
      }
    }
    legend {
      name
      value
      percentage
      actionsCount
    }
    startYear
  }
`);

graphql(`
  fragment EnvelopeUnit on envelope {
    id
    secondary_id
    area
    envelope_type_id
    insulation_material_name
    u_value
    insulation_thickness
    orientation
    inclination
  }
`);

graphql(`
  fragment EnergyConsumerForAction on energy_consumer {
    name
    id
    energy_consumer_type_id
    energy_consumer_technology_type_id
    construction_year
    efficiency
    energy_system_consumer_routes {
      id
      secondary_id
      energy_source_type_id
      energy_final
      energy_system {
        energy_system_type_id
      }
    }
  }
`);

graphql(`
  fragment EnergySystem on energy_system {
    id
    secondary_id
    energy_system_type_id
    energy_system_consumer_routes(where: { energy_consumer_id: { _is_null: false } }) {
      id
      secondary_id
      energy_consumer {
        ...EnergyConsumerForAction
      }
      energy_final
      energy_source_type_id
      energy_distribution {
        id
        last_hydraulic_balancing
        last_balancing_set_to_never
        heat_transfer_loss
        balancing_with_components
      }
    }
    energy_system_producer_routes {
      energy_source_type_id
      energy_producer {
        nominal_power
      }
    }
  }
`);

graphql(`
  fragment BuildingModel on building_model {
    id
    building {
      id
      year_constructed
    }
    energy_final
    energy_primary
    heating_demand
    envelope_units(order_by: { id: asc }) {
      ...EnvelopeUnit
    }
    energy_consumers {
      id
      energy_consumer_type_id
      energy_consumer_technology_type_id
      efficiency
      energy_system_consumer_routes {
        energy_system {
          energy_system_type_id
        }
        energy_final
      }
    }
    energy_distributions {
      id
      heat_transfer_loss
      last_hydraulic_balancing
      last_balancing_set_to_never
      balancing_with_components
    }
    energy_systems {
      ...EnergySystem
    }
    heat_transfer_by_ventilation
  }
`);

graphql(`
  fragment EnvelopeRenovation on renovation_envelope {
    new_envelope {
      id
      secondary_id
      area
      envelope_type_id
      insulation_material_name
      u_value
      insulation_lambda
      insulation_method_id
      insulation_thickness
      insulation_material_category_id
    }
    old_entity_id
    old_envelope {
      id
      secondary_id
      building_model {
        envelope_units(order_by: { id: asc }) {
          ...EnvelopeUnit
        }
      }
    }
  }
`);

graphql(`
  fragment ActionParameters on action {
    parameters {
      ... on CustomActionParametersUnionMember {
        __typename
        customActionParameters {
          ... on EnergySourceCustomActionParametersUnionMember {
            __typename
            energySourceCustomActionParameters {
              custom_name
              renovation_ids
              fraction
              energy_source_type
            }
          }
          ... on SystemCustomActionParametersUnionMember {
            __typename
            systemCustomActionParameters {
              system_type
              renovation_ids
              fraction
              custom_name
            }
          }
        }
      }
      ... on EnergySystemRouteParametersUnionMember {
        __typename
        energySystemRouteParameters {
          ...EnergySystemRouteRenovationParameterObject
        }
      }
      ... on SystemParametersUnionMember {
        __typename
        systemParameters {
          ...SystemRenovationParameterObject
        }
      }
      ... on SolarParametersUnionMember {
        __typename
        solarParameters {
          ...SolarRenovationParameterObject
        }
      }
      ... on HydraulicBalancingParametersUnionMember {
        __typename
        hydraulicBalancingParameters {
          ...HydraulicBalancingRenovationParameterObject
        }
      }
      ... on EnvelopeParametersUnionMember {
        __typename
        envelopeParameters {
          ...EnvelopeRenovationParameterObject
        }
      }
      ... on MaintenanceActionParametersUnionMember {
        __typename
        maintenanceActionParameters {
          custom_name
        }
      }
      ... on VentilationRenovationParametersUnionMember {
        __typename
        ventilationRenovationParameters {
          ...VentilationRenovationParameterObject
        }
      }
      ... on CreateVentilationRenovationParametersUnionMember {
        __typename
        createVentilationRenovationParameters {
          ...CreateVentilationRenovationParameterObject
        }
      }
      ... on ExclusionNoteParametersUnionMember {
        __typename
        exclusionNoteParameters {
          ...ExclusionNoteParameterObject
        }
      }
    }
  }
`);

graphql(`
  fragment Action on action {
    id
    created_at
    implementation_to
    order
    custom_action_type_id
    custom_name
    custom_saving_fraction
    bulk_action_id
    bulk_action {
      name
    }
    renovations {
      renovation_type_id
      cost_total
      cost_total_predium
      cost_total_custom
      use_custom_cost
    }
    ...ActionParameters
    renovations_energy_consumer {
      new_energy_consumer {
        ...EnergyConsumerForAction
      }
      old_energy_consumer {
        ...EnergyConsumerForAction
      }
    }

    renovations_envelope {
      ...EnvelopeRenovation
    }

    renovations_energy_routes {
      new_energy_system_route {
        id
        energy_source_type_id
        energy_system {
          energy_system_type_id
        }
      }
      old_energy_system_route {
        id
        energy_source_type_id
        energy_system {
          energy_system_type_id
        }
      }
    }

    renovations_energy_producer {
      new_energy_producer {
        name
        nominal_power
        module_peak_power
        module_angle
        solar_active_area
        roof_area_ratio
      }
    }

    building_models {
      ...BuildingModel
    }
  }
`);

graphql(`
  fragment EnergySystemRouteRenovationParameterObject on EnergySystemRouteRenovationParameterObject {
    energy_source_type
    emission_certificate_id
  }
`);

graphql(`
  fragment EnvelopeRenovationParameterObject on EnvelopeRenovationParameterObject {
    u_value
    renovation_ids
    material_name
    insulation_thickness
    insulation_method
    insulation_material_category
    insulation_lambda
    envelope_type
  }
`);

graphql(`
  fragment HydraulicBalancingRenovationParameterObject on HydraulicBalancingRenovationParameterObject {
    last_hydraulic_balancing
    costs_with_components
  }
`);

graphql(`
  fragment SolarRenovationParameterObject on SolarRenovationParameterObject {
    technology_name
    solar_active_area
    roof_area_ratio
    module_peak_power
    module_angle
  }
`);

graphql(`
  fragment SystemRenovationParameterObject on SystemRenovationParameterObject {
    consumer_technology_type
    efficiency
    energy_source_type
    renovation_ids
    system_type
    technology_name
    emission_certificate_id
  }
`);

graphql(`
  fragment CreateVentilationRenovationParameterObject on CreateVentilationRenovationParameterObject {
    consumer_technology_type
    final_energy
    efficiency
    number_of_units
  }
`);

graphql(`
  fragment VentilationRenovationParameterObject on VentilationRenovationParameterObject {
    renovation_ids
    consumer_technology_type
    final_energy
    efficiency
    number_of_units
  }
`);

graphql(`
  fragment ExclusionNoteParameterObject on ExclusionNoteParameterObject {
    note
  }
`);

graphql(`
  fragment ActionForRecommendedAction on action {
    id
    implementation_to
    renovations {
      renovation_type_id
    }
  }
`);

graphql(`
  fragment ActionPlanningActionSubsidy on action_subsidy {
    id
    subsidy {
      id
      title
      categories {
        subsidy_category_type
      }
    }
    type
    value
    action {
      id
      renovations {
        cost_total
        cost_total_predium
        renovation_type_id
      }
    }
  }
`);

graphql(`
  fragment BeforeAfterImpact on BeforeAfterDataWithImpactOutput {
    after
    after_total
    before
    before_total
    impact
    impact_total
  }
`);

graphql(`
  fragment ModificationImpactCrrem on ModificationImpactCrrem {
    analysisType
    co2_emissions {
      ...BeforeAfterImpact
    }
    final_energy {
      ...BeforeAfterImpact
    }
    primary_energy {
      ...BeforeAfterImpact
    }
    efficiency_class {
      before
      after
    }
    stranding_date {
      before
      after
      impact
    }
    energy_cost_total {
      after
      before
      impact
    }
    co2_cost_total {
      after
      before
      impact
    }
    heating_demand {
      ...BeforeAfterImpact
    }
  }
`);

graphql(`
  fragment ModificationImpactsNationalEnergyLaw on ModificationImpactNationalEnergyLaw {
    analysisType
    co2_emissions {
      ...BeforeAfterImpact
    }
    final_energy {
      ...BeforeAfterImpact
    }
    primary_energy {
      ...BeforeAfterImpact
    }
    efficiency_class {
      before
      after
    }
    energy_cost_total {
      after
      before
      impact
    }
    co2_cost_total {
      after
      before
      impact
    }
    heating_demand {
      ...BeforeAfterImpact
    }
  }
`);

graphql(`
  fragment RecommendedActionImpactOutput on RecommendedActionImpactCrremOutput {
    analysisType
    co2_cost_total {
      after
      before
      impact
    }
    co2_emissions {
      ...BeforeAfterImpact
    }
    efficiency_class {
      after
      before
    }
    energy_cost_total {
      after
      before
      impact
    }
    final_energy {
      ...BeforeAfterImpact
    }
    primary_energy {
      ...BeforeAfterImpact
    }
    stranding_date {
      after
      before
      impact
    }
    heating_demand {
      ...BeforeAfterImpact
    }
    total_estimated_cost
    co2_abatement_costs
    payback_period
  }
`);

graphql(`
  fragment RecommendedActionImpactNationalEnergyLawOutput on RecommendedActionImpactNationalEnergyLawOutput {
    analysisType
    co2_cost_total {
      after
      before
      impact
    }
    co2_emissions {
      ...BeforeAfterImpact
    }
    efficiency_class {
      after
      before
    }
    energy_cost_total {
      after
      before
      impact
    }
    final_energy {
      ...BeforeAfterImpact
    }
    primary_energy {
      ...BeforeAfterImpact
    }
    heating_demand {
      ...BeforeAfterImpact
    }
    total_estimated_cost
    co2_abatement_costs
    payback_period
  }
`);

graphql(`
  fragment RecommendedActionImpact on RecommendedActionImpactUnion {
    ...RecommendedActionImpactOutput
    ...RecommendedActionImpactNationalEnergyLawOutput
  }
`);

graphql(`
  fragment ModificationImpacts on ModificationImpactUnion {
    ...ModificationImpactCrrem
    ...ModificationImpactsNationalEnergyLaw
  }
`);

graphql(`
  fragment ActionImpactDataOutput on ActionImpactDataOutput {
    action_id
    total_renovation_cost
    total_subsidy_cost
    total_estimated_cost
    total_floor_area
    payback_period
    co2_abatement_costs
    modification_impact {
      ...ModificationImpactCrrem
      ...ModificationImpactsNationalEnergyLaw
    }
  }
`);

graphql(`
  fragment ActionPlanImpactDataOutput on ActionPlanImpactDataOutput {
    total_renovation_cost
    cost_by_renovation_category {
      ...CostByRenovationCategory
    }
    total_subsidy_cost
    total_estimated_cost
    total_floor_area
    modification_impact {
      ...ModificationImpactCrrem
      ...ModificationImpactsNationalEnergyLaw
    }
  }
`);

graphql(`
  fragment ActionPlanMetrics on ActionPlanMetricsOutput {
    action_plan {
      ...ActionPlanImpactDataOutput
    }
    actions {
      ...ActionImpactDataOutput
    }
  }
`);

graphql(`
  fragment ActionPlanningActionPlanActionPlanForCompare on action_plan {
    stable_id
    created_at
    scenario_version {
      cost_version {
        status
        version
        valid_from
      }
      scenario {
        name
        id
        owner {
          first_name
          last_name
          id
        }
        portfolio {
          id
          name
        }
      }
    }
    building {
      id
      address {
        street
        postal_code
        city
      }
    }
  }
`);

graphql(`
  fragment ActionPlanningActionPlanActionPlan on action_plan {
    stable_id
    created_at
    scenario_version {
      scenario {
        id
      }
    }
    action_plan_inconsistencies {
      energy_supplier_inconsistencies
    }
    building {
      id
      description
      monument_protection
      year_constructed
      address {
        street
        city
        country_id
        postal_code
      }
      areas {
        value
        type_of_use_id
        area_type_id
        class_of_use_id
      }
      active_building_model {
        ...BuildingModel
      }
      base_emission_factors {
        ...EmissionFactorsPerEnergySource
      }
      units
      subsidies {
        subsidy {
          ...DataCollectionBuildingSubsidy
        }
      }
      district_heating_availability_status {
        id: building_id
        district_heating_status
        district_heating_year
      }
      economic_unit {
        portfolio {
          name
        }
      }
    }
  }
`);

graphql(`
  fragment EnergySystemRouteRenovationParameter on EnergySystemRouteRenovationParameterOutput {
    __typename
    energy_source_type
  }
`);

graphql(`
  fragment EnvelopeRenovationParameter on EnvelopeRenovationParameterOutput {
    __typename
    envelope_type
    insulation_lambda
    insulation_material_category
    insulation_method
    insulation_thickness
    material_name
    u_value
  }
`);

graphql(`
  fragment HydraulicBalancingRenovationParameter on HydraulicBalancingRenovationParameterOutput {
    __typename
    costs_with_components
    last_hydraulic_balancing
  }
`);

graphql(`
  fragment CreateVentilationRenovationParameter on CreateVentilationRenovationParameterOutput {
    __typename
    ventilation_consumer_technology_type
    final_energy
    ventilation_efficiency
    number_of_units
  }
`);

graphql(`
  fragment UpdateVentilationRenovationParameter on UpdateVentilationRenovationParameterOutput {
    __typename
    renovation_ids
    ventilation_consumer_technology_type
    final_energy
    ventilation_efficiency
    number_of_units
  }
`);

graphql(`
  fragment SolarRenovationParameter on SolarRenovationParameterOutput {
    __typename
    module_angle
    module_peak_power
    roof_area_ratio
    solar_active_area
    technology_name
  }
`);

graphql(`
  fragment SystemRenovationParameter on SystemRenovationParameterOutput {
    __typename
    consumer_technology_type
    efficiency
    energy_source_type
    system_type
    technology_name
  }
`);

graphql(`
  fragment RecommendedAction on RecommendedAction {
    __typename
    actionImpact {
      ...RecommendedActionImpactOutput
      ...RecommendedActionImpactNationalEnergyLawOutput
    }
    parameters {
      __typename
      ... on EnergySystemRouteRenovationParameterOutput {
        ...EnergySystemRouteRenovationParameter
      }
      ... on SystemRenovationParameterOutput {
        ...SystemRenovationParameter
      }
      ... on SolarRenovationParameterOutput {
        ...SolarRenovationParameter
      }
      ... on HydraulicBalancingRenovationParameterOutput {
        ...HydraulicBalancingRenovationParameter
      }
      ... on EnvelopeRenovationParameterOutput {
        ...EnvelopeRenovationParameter
      }
      ... on CreateVentilationRenovationParameterOutput {
        ...CreateVentilationRenovationParameter
      }
      ... on UpdateVentilationRenovationParameterOutput {
        ...UpdateVentilationRenovationParameter
      }
    }
  }
`);

graphql(`
  fragment AnalyzedScenario on AnalyzeScenarioOutput {
    building_count
    cost_total
    co2_abatement_cost
    total_floor_area
    modification_impact {
      final_energy {
        ...BeforeAfterImpact
      }
      co2_emissions {
        ...BeforeAfterImpact
      }
      primary_energy {
        ...BeforeAfterImpact
      }
    }
  }
`);

// Bulk actions parameters fragment
graphql(`
  fragment BulkActionParameters on bulk_action {
    params {
      ... on CustomBulkActionParametersUnionMember {
        __typename
        customActionParameters {
          ... on EnergySourceCustomBulkActionParametersUnionMember {
            __typename
            energySourceCustomActionParameters {
              custom_name
              energy_source_type
              fraction
            }
          }
          ... on SystemCustomBulkActionParametersUnionMember {
            __typename
            systemCustomActionParameters {
              custom_name
              fraction
              system_type
            }
          }
        }
      }
      ... on EnergySystemRouteParametersBulkUnionMember {
        __typename
        energySystemRouteParameters {
          energy_source_type
          emission_certificate_id
        }
      }
      ... on EnvelopeParametersBulkUnionMember {
        __typename
        envelopeParameters {
          envelope_type
          insulation_lambda
          insulation_material_category
          insulation_method
          insulation_thickness
          material_name
          u_value
        }
      }
      ... on HydraulicBalancingParametersBulkUnionMember {
        __typename
        hydraulicBalancingParameters {
          costs_with_components
          last_hydraulic_balancing
        }
      }
      ... on LightingParametersBulkUnionMember {
        __typename
        lightingParameters {
          efficiency
        }
      }
      ... on SolarParametersBulkUnionMember {
        __typename
        solarParameters {
          module_angle
          module_peak_power
          roof_area_ratio
          technology_name
        }
      }
      ... on SystemParametersBulkUnionMember {
        __typename
        systemParameters {
          consumer_technology_type
          efficiency
          energy_source_type
          system_type
          technology_name
          emission_certificate_id
        }
      }
      ... on CreateVentilationRenovationParametersUnionMember {
        __typename
        createVentilationRenovationParameters {
          consumer_technology_type
          efficiency
          final_energy
          number_of_units
        }
      }
      ... on MaintenanceActionParametersUnionMember {
        __typename
        maintenanceActionParameters {
          custom_name
        }
      }
      ... on VentilationRenovationParametersUnionMember {
        __typename
        ventilationRenovationParameters {
          consumer_technology_type
          efficiency
          final_energy
          number_of_units
          renovation_ids
        }
      }
      ... on ExclusionNoteParametersUnionMember {
        __typename
        exclusionNoteParameters {
          note
        }
      }
    }
    custom_costs
    custom_costs_per_m2
  }
`);

// Bulk actions parameters fragment for bulk actions table
graphql(`
  fragment BulkActionParametersForTable on bulk_action {
    params {
      ... on CustomBulkActionParametersUnionMember {
        __typename
        customActionParameters {
          ... on EnergySourceCustomBulkActionParametersUnionMember {
            __typename
            energySourceCustomActionParameters {
              custom_name
            }
          }
          ... on SystemCustomBulkActionParametersUnionMember {
            __typename
            systemCustomActionParameters {
              custom_name
            }
          }
        }
      }
      ... on EnergySystemRouteParametersBulkUnionMember {
        __typename
        energySystemRouteParameters {
          energy_source_type
        }
      }
      ... on EnvelopeParametersBulkUnionMember {
        __typename
        envelopeParameters {
          envelope_type
        }
      }
      ... on HydraulicBalancingParametersBulkUnionMember {
        __typename
      }
      ... on LightingParametersBulkUnionMember {
        __typename
      }
      ... on SolarParametersBulkUnionMember {
        __typename
      }
      ... on SystemParametersBulkUnionMember {
        __typename
        systemParameters {
          system_type
          technology_name
        }
      }
      ... on CreateVentilationRenovationParametersUnionMember {
        __typename
      }
      ... on MaintenanceActionParametersUnionMember {
        __typename
        maintenanceActionParameters {
          custom_name
        }
      }
      ... on VentilationRenovationParametersUnionMember {
        __typename
      }
      ... on ExclusionNoteParametersUnionMember {
        __typename
        exclusionNoteParameters {
          note
        }
      }
    }
  }
`);

graphql(`
  fragment ActionPlanImpactTrendsWithTargetPathOutput on ActionPlanImpactTrendsWithTargetPathOutput {
    type
    after_actions_path
    before_actions_path
    target_path
    annotations {
      action_id
      annotation_type
      value
      year
    }
    stranding_ranges {
      end_year
      start_year
    }
    exclusion_note_implementation_year
  }
`);

graphql(`
  fragment ActionPlanImpactTrendsWithoutTargetPathOutput on ActionPlanImpactTrendsWithoutTargetPathOutput {
    type
    after_actions_path
    before_actions_path
    annotations {
      action_id
      annotation_type
      value
      year
    }
    exclusion_note_implementation_year
  }
`);

graphql(`
  fragment EmissionCertificate on emission_certificate {
    issuer
    emission_factor
    primary_energy_factor
    id
    emission_certificate_energy_source_type_id
    created_at
  }
`);

export const GET_SEARCH_BUILDINGS_FOR_CREATE_ACTION_PLAN = graphql(`
  query GetSearchBuildingsForCreateActionPlan(
    $search_string: String
    $portfolio_ids: [Int!] = null
    $limit: Int!
    $offset: Int!
    $orderBy: BuildingOrderByInput
    $scenario_id: Int!
    $include_new_construction: Boolean
  ) {
    searchBuildingsForCreateActionPlan(
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      portfolio_ids: $portfolio_ids
      scenario_id: $scenario_id
      search_string: $search_string
      include_new_construction: $include_new_construction
    ) {
      buildings {
        id
        street
        city
        postal_code
        is_new_construction
        has_energy_data
        action_planning_available
        efficiency_class
      }
      count
    }
  }
`);

export const GET_ACTIONPLAN = graphql(`
  query ActionPlanningActionPlanGetActionPlan($actionPlanStableId: Int!) {
    getActionPlanByStableId(stableId: $actionPlanStableId) {
      action_plan {
        ...ActionPlanningActionPlanActionPlan
      }
    }
  }
`);

export const GET_ACTIONPLAN_FOR_COMPARE = graphql(`
  query ActionPlanningActionPlanGetActionPlanForCompare($actionPlanStableId: Int!) {
    getActionPlanByStableId(stableId: $actionPlanStableId) {
      action_plan {
        ...ActionPlanningActionPlanActionPlanForCompare
      }
    }
  }
`);

export const ACTION_PLAN_PROCESSING_SUBSCRIPTION = graphql(`
  subscription ActionPlanProcessingSubscription($actionPlanIds: [Int!]!) {
    action_plan(where: { stable_id: { _in: $actionPlanIds } }) {
      stable_id
      processing
    }
  }
`);

export const GET_SUBSIDIES = graphql(`
  query ActionPlanningGetSubsidies($actionPlanStableId: Int!) {
    getActionPlanByStableId(stableId: $actionPlanStableId) {
      action_plan {
        actions {
          subsidies {
            ...ActionPlanningActionSubsidy
          }
        }
      }
    }
  }
`);

export const GET_SCENARIO_COST_UPDATE_SUBSCRIPTION = graphql(`
  subscription ActionPlanningScenarioGetScenarioCostUpdateSubscription($scenarioId: Int!) {
    scenario_by_pk(id: $scenarioId) {
      cost_update_in_progress
      scenario_versions(where: { status: { _eq: ACTIVE } }) {
        cost_version {
          version
          status
        }
      }
    }
  }
`);

export const DUPLICATE_SCENARIO_SUBSCRIPTION = graphql(`
  subscription DuplicateScenarioSubscription($scenarioId: Int!) {
    scenario_by_pk(id: $scenarioId) {
      duplication_in_progress
    }
  }
`);

export const GET_ACTION_PLAN_LIST = graphql(`
  query GetActionPlanList(
    $scenarioId: Int!
    $limit: Int
    $offset: Int
    $orderBy: ActionPlanListOrderByInput
    $searchString: String
    $emissionFactorSource: emission_factor_source_enum
    $referenceAreaType: analysis_reference_area_enum
    $buildingFilter: BuildingFilter
  ) {
    getActionPlanList(
      scenarioId: $scenarioId
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      searchString: $searchString
      emissionFactorSource: $emissionFactorSource
      referenceAreaType: $referenceAreaType
      buildingFilter: $buildingFilter
    ) {
      count
      action_plans {
        id
        modification_impact {
          co2_emissions {
            ...BeforeAfterImpact
          }
          final_energy {
            ...BeforeAfterImpact
          }
          primary_energy {
            ...BeforeAfterImpact
          }
          efficiency_class {
            before
            after
          }
        }
        action_plan {
          processing
          stable_id
          created_at
          active_scenario {
            scenario {
              portfolio_id
            }
          }
          building {
            id
            description
            has_write_permission
            address {
              street
              city
              postal_code
              country_id
            }
            areas(where: { area_type_id: { _eq: EBF } }) {
              area_type_id
              type_of_use_id
              class_of_use_id
              value
            }
            economic_unit {
              portfolio_id
            }
          }
          actions_aggregate {
            aggregate {
              count
              max {
                updated_at
              }
              sum {
                cost_total
              }
            }
            nodes {
              renovations(limit: 1) {
                renovation_type_id
              }
              implementation_to
            }
          }
        }
      }
    }
  }
`);

graphql(`
  fragment ActionPlanningScenariosScenario on ScenarioListItemOutput {
    id
    name
    action_plan_count
    created_at
    description
    duplication_in_progress
    is_archived
    owner {
      id
      first_name
      last_name
    }
    portfolio {
      id
      name
    }
    can_edit
    total_investment
  }
`);

export const GET_SCENARIOS = graphql(`
  query ActionPlanningScenariosAllScenarios(
    $where: ScenarioWhere
    $offset: Int
    $limit: Int
    $orderBy: ScenarioOrderByInput
  ) {
    getScenarioList(where: $where, offset: $offset, limit: $limit, orderBy: $orderBy) {
      scenarios {
        ...ActionPlanningScenariosScenario
      }
      count
    }
  }
`);

export const GET_SCENARIO = graphql(`
  query ActionPlanningScenarioGetScenario($scenario_id: Int!) {
    getScenario(scenario_id: $scenario_id) {
      id
      name
      description
      created_at
      updated_at
      duplication_in_progress
      is_archived
      owner {
        id
        first_name
        last_name
      }
      portfolio {
        id
        name
      }
      scenario_versions {
        id
        status
        cost_version {
          version
          status
          valid_from
        }
      }
      building_count
      can_edit
    }
  }
`);

export const GET_SCENARIOS_BY_PORTFOLIO_ID = graphql(`
  query ActionPlanningScenariosByPortfolioId($where: scenario_bool_exp!) {
    scenarios: scenario(where: $where) {
      id
      name
    }
  }
`);

export const GET_SCENARIO_ACTION_PLAN_AND_ACTIONS_COUNTS = graphql(`
  query GetScenarioActionPlanAndActionsCounts($scenario_id: Int!) {
    getScenarioActionPlanAndActionsCounts(scenario_id: $scenario_id) {
      action_plan_count
      scenario_actions_count
    }
  }
`);

export const GET_PORTFOLIOS_WITH_SCENARIOS = graphql(`
  query ActionPlanningScenariosGetPortfoliosWithScenarios {
    scenario(where: { archived_at: { _is_null: true } }, distinct_on: portfolio_id, order_by: { portfolio_id: asc }) {
      portfolio {
        id
        name
      }
    }
  }
`);

export const GET_PORTFOLIOS_WITH_ARCHIVED_SCENARIOS = graphql(`
  query ActionPlanningScenariosGetPortfoliosWithArchivedScenarios {
    scenario(where: { archived_at: { _is_null: false } }, distinct_on: portfolio_id, order_by: { portfolio_id: asc }) {
      portfolio {
        id
        name
      }
    }
  }
`);

export const GET_SCENARIOS_BASIC_DETAILS = graphql(`
  query ActionPlanningScenariosNames {
    scenario(where: { archived_at: { _is_null: true } }) {
      name
      id
    }
  }
`);

graphql(`
  fragment SearchScenario on scenario {
    id
    name
  }
`);

graphql(`
  fragment SearchActionPlan on action_plan {
    stable_id
    scenario_version {
      scenario {
        id
        name
      }
    }
    building {
      id
      customer_external_identifier
      address {
        street
        city
        postal_code
      }
    }
  }
`);

export const SEARCH_SCENARIOS = graphql(`
  query SearchScenarios($limit: Int!, $offset: Int!, $text: String!) {
    scenario(
      where: { name: { _ilike: $text }, archived_at: { _is_null: true } }
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      ...SearchScenario
    }
  }
`);

export const SEARCH_ACTION_PLANS = graphql(`
  query SearchActionPlans($limit: Int!, $offset: Int!, $text: String!) {
    action_plan(
      where: {
        _or: [
          { building: { address: { street: { _ilike: $text } } } }
          { building: { address: { city: { _ilike: $text } } } }
          { building: { address: { postal_code: { _ilike: $text } } } }
          { building: { customer_external_identifier: { _ilike: $text } } }
        ]
      }
      distinct_on: [stable_id]
      limit: $limit
      offset: $offset
      order_by: [{ stable_id: asc }, { building: { address: { street: asc } } }]
    ) {
      ...SearchActionPlan
    }
  }
`);

export const GET_ANALYZED_SCENARIO = graphql(`
  query GetAnalyzeScenario(
    $scenario_id: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emission_factor_source: emission_factor_source_enum
    $includeNewConstruction: Boolean
  ) {
    getAnalyzedScenario(
      scenario_id: $scenario_id
      referenceAreaType: $referenceAreaType
      emission_factor_source: $emission_factor_source
      includeNewConstruction: $includeNewConstruction
    ) {
      ...AnalyzedScenario
    }
  }
`);

export const GET_BUILDINGS = graphql(`
  query CreateActionPlanModalGetBuildings(
    $where: building_bool_exp!
    $limit: Int!
    $offset: Int!
    $orderBy: [building_order_by!]
  ) {
    building(where: $where, limit: $limit, offset: $offset, order_by: $orderBy) {
      __typename
      id
      address {
        __typename
        id
        street
        city
        postal_code
        country_id
      }
      active_building_model {
        id
        action_planning_available
        efficiency_class_id
      }
    }
    building_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const QUEUE_EXPORT_ACTION_PLAN_PDF = graphql(`
  mutation QueueExportActionPlanPdf(
    $actionPlanStableId: Int!
    $title: String
    $description: String
    $useCustomTargetPath: Boolean
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    queueExportActionPlanAsPdf(
      actionPlanStableId: $actionPlanStableId
      title: $title
      description: $description
      useCustomTargetPath: $useCustomTargetPath
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      asyncJobId
    }
  }
`);

export const GET_ACTION_PLAN_METRICS = graphql(`
  query GetActionsPlanMetrics(
    $actionPlanStableId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getActionPlanMetrics(
      stable_id: $actionPlanStableId
      reference_area_type: $referenceAreaType
      emission_factor_source: $emissionFactorSource
    ) {
      ...ActionPlanMetrics
    }
  }
`);

export const GET_ACTION_PLAN_IMPACT_TRENDS = graphql(`
  query GetActionsPlanImpactTrends(
    $actionPlanStableId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getActionPlanImpactTrends(
      stableId: $actionPlanStableId
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      co2_emissions {
        ...ActionPlanImpactTrendsWithTargetPathOutput
        ...ActionPlanImpactTrendsWithoutTargetPathOutput
      }
      primary_energy {
        after_actions_path
        before_actions_path
        annotations {
          action_id
          annotation_type
          value
          year
        }
        exclusion_note_implementation_year
      }
      final_energy {
        ...ActionPlanImpactTrendsWithTargetPathOutput
        ...ActionPlanImpactTrendsWithoutTargetPathOutput
      }
      trend_start_year
      custom_targets {
        ...CustomTargets
      }
      total_investment_cost
    }
  }
`);

export const GET_ACTIONS_FOR_ACTION_PLAN = graphql(`
  query GetActionsForActionPlan($actionPlanStableId: Int!) {
    getActionPlanByStableId(stableId: $actionPlanStableId) {
      action_plan {
        actions(order_by: { order: asc }) {
          ...Action
        }
      }
    }
  }
`);

export const GET_STRANDING_YEAR_DISTRIBUTION = graphql(`
  query GetStrandingYearDistribution(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
  ) {
    getStrandingYearDistribution(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
    ) {
      after
      before
      buildingCounts
      startYear
    }
  }
`);

export const GET_HEATING_ENERGY_SOURCE_DEVELOPMENT = graphql(`
  query GetHeatingEnergySourceDevelopment(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
  ) {
    getHeatingEnergySourceDevelopment(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
    ) {
      startYear
      data {
        type
        path
      }
    }
  }
`);

export const GET_FLOOR_AREA_DEVELOPMENT = graphql(`
  query GetFloorAreaDevelopment(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
    $includeNewConstruction: Boolean
  ) {
    getFloorAreaDevelopment(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
      includeNewConstruction: $includeNewConstruction
    ) {
      area
      totalBuildings
      newConstructionBuildings
      excludedSincePreviousYear
      startYear
    }
  }
`);

export const GET_EFFICIENCY_CLASS_SPLIT_DEVELOPMENT = graphql(`
  query GetEfficiencyClassSplitDevelopment(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
  ) {
    getEfficiencyClassSplitDevelopment(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
    ) {
      startYear
      data {
        efficiencyClass
        path
      }
    }
  }
`);

export const GET_ACTIONS_FOR_RECOMMENDED_ACTIONS = graphql(`
  query GetActionsForRecommendedActions($actionPlanStableId: Int!) {
    getActionPlanByStableId(stableId: $actionPlanStableId) {
      action_plan {
        actions(order_by: { order: asc }) {
          ...ActionForRecommendedAction
        }
      }
    }
  }
`);

export const GET_RECOMMENDED_ACTIONS_FOR_BUILDING_MODEL = graphql(`
  query GetRecommendedActionsForBuildingModel(
    $action_plan_stable_id: Int!
    $implementation_date: DateTime!
    $sorting: recommended_action_sorting_enum!
    $emission_source_type: emission_factor_source_enum
    $reference_area_type: analysis_reference_area_enum
  ) {
    recommendActions(
      action_plan_stable_id: $action_plan_stable_id
      implementation_date: $implementation_date
      sorting: $sorting
      emission_source_type: $emission_source_type
      reference_area_type: $reference_area_type
    ) {
      recommendedActions {
        ...RecommendedAction
      }
    }
  }
`);

export const GET_EDIT_ACTION_STATUS_QUO = graphql(`
  query GetEditActionStatusQuo(
    $action_id: Int!
    $implementation_to: DateTime!
    $emission_factor_source: emission_factor_source_enum
    $reference_area_type: analysis_reference_area_enum
  ) {
    getEditStatusQuo(
      action_id: $action_id
      new_date: $implementation_to
      emission_factor_source: $emission_factor_source
      reference_area_type: $reference_area_type
    ) {
      ...SimulateActionStatusQuoOutputResponse
    }
  }
`);

export const GET_CREATE_ACTION_STATUS_QUO = graphql(`
  query GetCreateActionStatusQuo(
    $action_plan_stable_id: Int!
    $implementation_to: DateTime!
    $emission_factor_source: emission_factor_source_enum
    $reference_area_type: analysis_reference_area_enum
  ) {
    getCreateStatusQuo(
      action_plan_stable_id: $action_plan_stable_id
      new_date: $implementation_to
      emission_factor_source: $emission_factor_source
      reference_area_type: $reference_area_type
    ) {
      ...SimulateActionStatusQuoOutputResponse
    }
  }
`);

export const GET_BULK_ACTION_ITEMS = graphql(`
  query GetBulkActionItems(
    $limit: Int!
    $offset: Int!
    $where: bulk_actions_overview_bool_exp
    $orderBy: [bulk_actions_overview_order_by!]
  ) {
    bulk_actions_overview(where: $where, limit: $limit, offset: $offset, order_by: $orderBy) {
      id: action_plan_id
      action_plan_status
      bulk_action_id
      action_plan {
        stable_id
        building {
          id
          address {
            city
            postal_code
            street
          }
        }
      }
    }
    bulk_actions_overview_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_BULK_ACTION_PARAMETERS = graphql(`
  query GetBulkActionParameters($id: Int!) {
    bulk_actions_overview(where: { bulk_action_id: { _eq: $id } }) {
      bulk_action {
        name
        ...BulkActionParameters
        implementation_date
      }
    }
    total_count: bulk_actions_overview_aggregate(where: { bulk_action_id: { _eq: $id } }) {
      aggregate {
        count
      }
    }
    planned_count: bulk_actions_overview_aggregate(
      where: { bulk_action_id: { _eq: $id }, action_plan_status: { _eq: "PLANNED" } }
    ) {
      aggregate {
        count
      }
    }
    not_planned_count: bulk_actions_overview_aggregate(
      where: { bulk_action_id: { _eq: $id }, action_plan_status: { _eq: "FAILED" } }
    ) {
      aggregate {
        count
      }
    }
    deletion_failled_count: bulk_actions_overview_aggregate(
      where: { bulk_action_id: { _eq: $id }, action_plan_status: { _eq: "DELETION_FAILED" } }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ACTION_PLAN_TABS_COUNT = graphql(`
  query GetActionPlanTabsCount($scenarioId: Int!) {
    active_action_plans_aggregate(where: { scenario: { id: { _eq: $scenarioId } } }) {
      aggregate {
        count
      }
    }

    active_bulk_actions_aggregate(where: { scenario: { id: { _eq: $scenarioId } } }) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_BULK_ACTIONS = graphql(`
  query GetBulkActions(
    $limit: Int!
    $offset: Int!
    $where: active_bulk_actions_bool_exp
    $orderBy: [active_bulk_actions_order_by!]
  ) {
    active_bulk_actions(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      bulk_action {
        id
        name
        implementation_date
        summary {
          total_cost
          total_count
          planned_count
          pending_count
          failed_count
          deletion_pending_count
          deletion_failed_count
        }
        ...BulkActionParametersForTable
      }
    }
    active_bulk_actions_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_BULK_ACTION_SUMMARY = graphql(`
  subscription GetBulkActionSummary($id: Int) {
    bulk_action(where: { id: { _eq: $id } }) {
      summary {
        total_count
        planned_count
        failed_count
        total_cost
        pending_count
        deletion_pending_count
        deletion_failed_count
      }
    }
  }
`);

export const GET_ACTION_PLAN_PROCESSING_STATUS = graphql(`
  subscription GetActionPlanProcessingStatus($stableId: Int!) {
    bulk_actions_overview(where: { action_plan: { stable_id: { _eq: $stableId } } }) {
      action_plan_status
    }
  }
`);

export const GET_SCENARIO_BULK_ACTION_PROCESSING_STATUS = graphql(`
  subscription GetScenarioBulkActionProcessingStatus($scenarioId: Int!) {
    active_bulk_actions(where: { scenario: { id: { _eq: $scenarioId } } }) {
      bulk_action {
        summary {
          pending_count
          deletion_pending_count
        }
      }
    }
  }
`);

export const GET_DISTRICT_HEATING_EMISSION_CERTIFICATES = graphql(`
  query getDistrictHeatingEmissionCertificates {
    emission_certificate(
      where: {
        emission_certificate_energy_source_type_id: { _eq: DISTRICT_HEATING }
        emission_factor: { _is_null: false }
        primary_energy_factor: { _is_null: false }
      }
      order_by: { created_at: asc }
    ) {
      ...EmissionCertificate
    }
  }
`);

export const GET_GREEN_ELECTRICITY_EMISSION_CERTIFICATES = graphql(`
  query getGreenElectricityEmissionCertificates {
    emission_certificate(
      where: {
        emission_certificate_energy_source_type_id: { _eq: ELECTRICITY_GREEN }
        emission_factor: { _is_null: false }
        primary_energy_factor: { _is_null: false }
      }
      order_by: { created_at: asc }
    ) {
      ...EmissionCertificate
    }
  }
`);

export const GET_ACTION_PLANS_FOR_BUILDING = graphql(`
  query GetActionPlansForBuilding($buildingId: Int!) {
    active_action_plans(
      where: { building: { id: { _eq: $buildingId } }, scenario: { archived_at: { _is_null: true } } }
      order_by: { action_plan: { created_at: desc } }
    ) {
      action_plan {
        id: stable_id
        stable_id
        building_id
        created_at
      }
      scenario {
        id
        name
        owner {
          id
          first_name
          last_name
        }
      }
    }
  }
`);

export const GET_BUILDINGS_FOR_BULK_ACTION = graphql(`
  query GetBuildingsForBulkAction(
    $buildingFilter: BuildingFilter!
    $buildingFilterDisabled: BuildingFilter! = {}
    $limit: Int
    $offset: Int
    $orderBy: BuildingOrderByInput
    $skipDisabled: Boolean! = false
  ) {
    getBuildingList(filter: $buildingFilter, limit: $limit, offset: $offset, orderBy: $orderBy) {
      buildings {
        id
        is_new_construction
        energy_data {
          energy_final
        }
        building {
          id
          address {
            id
            street
            city
            postal_code
            country_id
          }
          areas(where: { area_type_id: { _eq: EBF } }) {
            id
            type_of_use_id
          }
          economic_unit {
            portfolio {
              name
            }
          }
        }
      }
      count
    }
    getBuildingList_disabled: getBuildingList(filter: $buildingFilterDisabled, orderBy: $orderBy)
      @skip(if: $skipDisabled) {
      buildings {
        id
      }
    }
  }
`);

export const GET_BUILDINGS_FOR_RECREATE_TABLE_SELECTION = graphql(`
  query GetBuildingsForRecreateTableSelection($portfolioIds: [Int!], $bulkActionId: Int!) {
    getBuildingList(filter: { portfolio_ids: $portfolioIds }) {
      buildings {
        id
      }
    }
    getBuildingList_disabled: getBuildingList(
      filter: {
        portfolio_ids: $portfolioIds
        boolean: [{ field: action_planning_available, operator: equals, value: false }]
      }
    ) {
      buildings {
        id
      }
    }
    bulk_actions_overview(where: { bulk_action_id: { _eq: $bulkActionId } }) {
      action_plan {
        building_id
      }
    }
  }
`);

export const GET_CO2_EMISSIONS_IMPACT_TREND = graphql(`
  query GetCo2EmissionsImpactTrend(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
    $includeNewConstruction: Boolean
  ) {
    getCo2EmissionsImpactTrend(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
      includeNewConstruction: $includeNewConstruction
    ) {
      after
      before
      startYear
      target_path
      custom_target {
        year
        reached
        targetValue
      }
    }
  }
`);

export const GET_FINAL_ENERGY_IMPACT_TREND = graphql(`
  query GetFinalEnergyImpactTrend(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
    $includeNewConstruction: Boolean
  ) {
    getFinalEnergyImpactTrend(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
      includeNewConstruction: $includeNewConstruction
    ) {
      after
      before
      startYear
      target_path
      custom_target {
        year
        reached
        targetValue
      }
    }
  }
`);

export const GET_PRIMARY_ENERGY_IMPACT_TREND = graphql(`
  query GetPrimaryEnergyImpactTrend(
    $scenarioId: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
    $includeNewConstruction: Boolean
  ) {
    getPrimaryEnergyImpactTrend(
      scenario_id: $scenarioId
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
      includeNewConstruction: $includeNewConstruction
    ) {
      after
      before
      startYear
      custom_target {
        year
        reached
        targetValue
      }
    }
  }
`);

export const GET_COST_BY_RENOVATION_CATEGORY = graphql(`
  query GetCostByRenovationCategory($scenarioId: Int!) {
    getCostByRenovationCategory(scenario_id: $scenarioId) {
      ...CostByRenovationCategory
    }
  }
`);

export const CAN_DELETE_BULK_ACTION = graphql(`
  query CanDeleteBulkAction($id: Int!) {
    canDeleteBulkAction(id: $id)
  }
`);

export const CAN_EDIT_BULK_ACTION = graphql(`
  query CanEditBulkAction($id: Int!) {
    canEditBulkAction(id: $id)
  }
`);

export const GET_SCENARIO_INVESTMENT_COST = graphql(`
  query GetScenarioInvestmentCost(
    $scenario_id: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionSourceType: emission_factor_source_enum
  ) {
    getScenarioInvestmentCost(
      scenario_id: $scenario_id
      referenceAreaType: $referenceAreaType
      emissionSourceType: $emissionSourceType
    ) {
      cost
      startYear
    }
  }
`);

export const GET_ACTION_PLAN_INVESTMENT_COST = graphql(`
  query GetActionPlanInvestmentCost($stable_id: Int!) {
    getActionPlanInvestmentCost(stable_id: $stable_id) {
      cost
      startYear
    }
  }
`);

export const GET_SCENARIO_CUSTOM_TARGET_PATH = graphql(`
  query GetScenarioCustomTargetPath($scenario_id: Int!) {
    getScenarioCustomTargetPath(scenario_id: $scenario_id) {
      targetPath
    }
  }
`);

export const GET_ACTION_PLAN_CUSTOM_TARGET_PATH = graphql(`
  query GetActionPlanCustomTargetPath($stable_id: Int!) {
    getActionPlanCustomTargetPath(stable_id: $stable_id) {
      targetPath
    }
  }
`);
